import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PitchListSidebar } from 'components/sections/pitch-list/sidebar';
import { AimingContext } from 'contexts/aiming.context';
import { GameDataContext } from 'contexts/game-data.context';
import { MachineContext } from 'contexts/machine.context';
import { ResetPlateMode } from 'enums/machine.enums';
import { VideoChangedFn } from 'interfaces/function-types';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { DeliveryType } from 'lib_ts/enums/pitches.enums';
import { clone } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import useMiscStore from 'stores/use-misc-store';
import { useVideosStore } from 'stores/use-videos-store';

const COMPONENT_NAME = 'GameDataPitchesSidebar';

export const GameDataPitchesSidebar = () => {
  const deliveryDict = useMiscStore((s) => s.deliveryDict);

  const { resetMSHash } = useContext(MachineContext);
  const { pitchBP, setAndSendForBP } = useContext(AimingContext);
  const { openAddDialog } = useContext(GameDataContext);

  const delivery = useMemo(
    () => (pitchBP ? deliveryDict[pitchBP._id] : undefined),
    [pitchBP, deliveryDict]
  );

  const handleVideoChange = useCallback<VideoChangedFn>(
    (videoID) => {
      if (!pitchBP) {
        return;
      }

      if (VideoHelper.getPitchDeliveryVideoID(pitchBP, delivery) === videoID) {
        // no change
        return;
      }

      if (videoID) {
        const video = useVideosStore
          .getState()
          .videos.find((v) => v._id === videoID);

        if (!video) {
          NotifyHelper.error({
            message_md: `Video \`${videoID}\` does not exist in context. Please try again.`,
          });
          return;
        }
      }

      const nextPitchBP = clone(pitchBP);

      switch (delivery) {
        case DeliveryType.Stretch: {
          nextPitchBP.stretchVideoID = videoID;
          break;
        }

        case DeliveryType.Windup:
        default: {
          nextPitchBP.windupVideoID = videoID;
          break;
        }
      }

      setAndSendForBP({
        pitch: nextPitchBP,
        resetPlate: ResetPlateMode.Skip,
        sendConfig: undefined,
      });

      resetMSHash();
    },
    [pitchBP, delivery, setAndSendForBP, resetMSHash]
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <PitchListSidebar
        onVideoChanged={handleVideoChange}
        handleSave={(pitch) => openAddDialog({ pitches: [pitch] })}
        enableAddToList
      />
    </ErrorBoundary>
  );
};
